import { AlertCircle, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ChevronUp, ChevronDown, ChevronsUpDown, Loader2, Shield, RefreshCw, LayoutGrid, List } from "lucide-react";
import {
  Component,
  Fragment,
  type ErrorInfo,
  type ReactNode,
  cloneElement,
  isValidElement,
  useEffect,
  useRef,
  useState,
} from "react";
import { useToastStore } from "../../stores/toastStore";
import { LoadingOverlay } from "./Skeleton";
import { RowsPerCardSelect } from "./RowsPerCardSelect";
import { NumericInput, type NumericInputProps } from "./g360/DataTable";
import { DEFAULT_TABLE_PAGE_SIZE } from "../../utils/formatters";
import { Tip } from "./ui/tooltip";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";
export { NumericInput, type NumericInputProps };
function nodeText(node: ReactNode, depth = 0): string {
  if (node === null || node === undefined || typeof node === "boolean")
    return "";
  if (typeof node === "string") return node;
  if (typeof node === "number") return String(node);
  if (depth > 8) return "";
  if (Array.isArray(node))
    return node.map((n) => nodeText(n, depth + 1)).join(" ");
  if (typeof node === "object" && "props" in node) {
    const props = (node as { props?: { children?: ReactNode; value?: unknown } }).props;
    const childText = nodeText(props?.children, depth + 1);
    if (childText) return childText;
    if (props?.value !== undefined && props?.value !== null && typeof props.value !== "object") {
      return String(props.value);
    }
  }
  return "";
}
const flatten = (s: string) => s.replace(/\s+/g, " ").trim();
export const DEFAULT_MAX_CELL_CHARS = 100;
const TRUNCATION_SUFFIX = "...";
function truncateString(s: string, left: number): [string, number] {
  if (left <= 0) return ["", 0];
  if (s.length <= left) return [s, left - s.length];
  return [s.slice(0, left).replace(/\s+$/, "") + TRUNCATION_SUFFIX, 0];
}
export function truncateNode(
  node: ReactNode,
  left: number,
): [ReactNode, number] {
  if (node === null || node === undefined || typeof node === "boolean")
    return [node, left];
  if (typeof node === "string") return truncateString(node, left);
  if (typeof node === "number") return truncateString(String(node), left);
  if (left <= 0) return [null, 0];
  if (Array.isArray(node)) {
    const out: ReactNode[] = [];
    let rest = left;
    node.forEach((child) => {
      const [next, r] = truncateNode(child, rest);
      rest = r;
      out.push(next);
    });
    return [out, rest];
  }
  if (isValidElement(node)) {
    const children = (node.props as { children?: ReactNode })?.children;
    if (children === undefined) return [node, left];
    const [nextChildren, rest] = truncateNode(children, left);
    return [cloneElement(node, undefined, nextChildren), rest];
  }
  return [node, left];
}
export function isEmptyContent(v: unknown): boolean {
  if (v === null || v === undefined) return true;
  if (v === false) return true;
  if (typeof v === "string") return v.trim() === "";
  if (Array.isArray(v)) return v.length === 0 || v.every(isEmptyContent);
  return false;
}
export function TooltipCell({
  tooltip,
  clampClass,
  wrapperClass = "block w-full break-words [word-break:normal]",
  mode = "always",
  children,
}: {
  tooltip?: string;
  clampClass?: string;
  wrapperClass?: string;
  mode?: "always" | "overflow";
  children: ReactNode;
}) {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);
  const base = clampClass
    ? wrapperClass.replace(/(^|\s)block(?=\s|$)/, " ")
    : wrapperClass;
  const triggerCls = `${base} ${clampClass ?? ""}`.replace(/\s+/g, " ").trim();
  if (!tooltip) {
    return clampClass ? (
      <span className={triggerCls}>{children}</span>
    ) : (
      <>{children}</>
    );
  }
  const isOverflowing = () => {
    const el = triggerRef.current;
    if (!el) return false;
    const clipped = (n: HTMLElement) =>
      n.scrollWidth > n.clientWidth + 1 || n.scrollHeight > n.clientHeight + 1;
    if (clipped(el)) return true;
    if (
      Array.from(el.querySelectorAll("*")).some(
        (n) => n instanceof HTMLElement && clipped(n),
      )
    )
      return true;
    let node: HTMLElement | null = el.parentElement;
    while (node && node !== document.body) {
      const style = getComputedStyle(node);
      if (/hidden|clip|auto|scroll/.test(style.overflowX + style.overflowY)) {
        const box = el.getBoundingClientRect();
        const clip = node.getBoundingClientRect();
        return box.right > clip.right + 1 || box.bottom > clip.bottom + 1;
      }
      node = node.parentElement;
    }
    return false;
  };
  return (
    <Tooltip
      open={open}
      onOpenChange={(next) =>
        setOpen(next ? mode === "always" || isOverflowing() : false)
      }
    >
      <TooltipTrigger asChild>
        <span ref={triggerRef} className={triggerCls}>
          {children}
        </span>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        sideOffset={6}
        className="max-w-md text-left whitespace-pre-wrap break-words pointer-events-none"
      >
        {tooltip}
      </TooltipContent>
    </Tooltip>
  );
}
const SORT_ARROW_UP = String.fromCharCode(0x25B2);
const SORT_ARROW_DOWN = String.fromCharCode(0x25BC);
export interface Column<T> {
  key: string;
  header: ReactNode;
  render?: (row: T, index: number) => ReactNode;
  align?: "left" | "center" | "right";
  headerAlign?: "left" | "center" | "right";
  width?: string;
  minWidth?: number;
  headerClassName?: string;
  cellClassName?: string;
  sortable?: boolean;
  /** Right-aligned sortable headers place the sort icon before the label by default;
   *  set true to place it after the label instead (icon stays on the outer edge otherwise). */
  sortIconAfterText?: boolean;
  ellipsis?: boolean | 1 | 2 | 3;
  tooltip?: ((row: T) => string | undefined) | string | false;
  headerTooltip?: string | false;
  maxChars?: number | false;
  emptyValue?: ReactNode;
  overflow?: "hidden" | "visible";
  wrapperClass?: string;
}
export interface ServerSideResponse<T = unknown> {
  draw?: number;
  recordsTotal?: number;
  recordsFiltered?: number;
  data?: T[] | unknown;
  error?: string;
}
export interface NormalizedServerResponse<T> {
  rows: T[];
  recordsTotal: number;
  recordsFiltered: number;
  countsKnown: boolean;
  draw: number;
  error: string | null;
  errorKind: "none" | "shape" | "server";
  stale: boolean;
}
function describeValue(v: unknown): string {
  if (v === null) return "null";
  if (v === undefined) return "nothing";
  if (Array.isArray(v)) return "an array";
  if (typeof v === "object") return "an object";
  return `a ${typeof v}`;
}
function toCount(v: unknown, fallback: number): number {
  return typeof v === "number" && Number.isFinite(v) && v >= 0
    ? Math.floor(v)
    : fallback;
}
export function normalizeServerResponse<T>(
  res: ServerSideResponse<T> | T[] | null | undefined,
  expectedDraw?: number,
): NormalizedServerResponse<T> {
  const blank = {
    rows: [] as T[],
    recordsTotal: 0,
    recordsFiltered: 0,
    countsKnown: false,
    draw: 0,
    stale: false,
  };
  if (res === null || res === undefined) {
    return {
      ...blank,
      error: "No response was received from the server.",
      errorKind: "shape",
    };
  }
  if (Array.isArray(res)) {
    const rows = res as T[];
    return {
      rows,
      recordsTotal: rows.length,
      recordsFiltered: rows.length,
      countsKnown: false,
      draw: 0,
      stale: false,
      error: null,
      errorKind: "none",
    };
  }
  if (typeof res !== "object") {
    return {
      ...blank,
      error: `Expected a server response object, but received ${describeValue(res)}.`,
      errorKind: "shape",
    };
  }
  const draw = toCount(res.draw, 0);
  const stale =
    expectedDraw !== undefined &&
    typeof res.draw === "number" &&
    draw < expectedDraw;
  if (typeof res.error === "string" && res.error.trim()) {
    return {
      ...blank,
      draw,
      stale,
      error: res.error.trim(),
      errorKind: "server",
    };
  }
  if (!Array.isArray(res.data)) {
    return {
      ...blank,
      draw,
      stale,
      error: `Table data must be an array, but the server sent ${describeValue(res.data)}.`,
      errorKind: "shape",
    };
  }
  const rows = res.data as T[];
  const recordsFiltered = toCount(res.recordsFiltered, rows.length);
  const recordsTotal = toCount(res.recordsTotal, recordsFiltered);
  return {
    rows,
    recordsTotal,
    recordsFiltered,
    countsKnown:
      typeof res.recordsFiltered === "number" ||
      typeof res.recordsTotal === "number",
    draw,
    stale,
    error: null,
    errorKind: "none",
  };
}

/** Leading "#" column; `offset` is the number of rows on earlier pages. */
export function rowNumberColumn<T>(offset: number): Column<T> {
  return {
    key: "rowNumber",
    header: "#",
    align: "center",
    sortable: false,
    width: "60px",
    minWidth: 50,
    render: (_row, index) => (
      <span className="text-xs font-normal text-foreground tabular-nums">{offset + index + 1}</span>
    ),
  };
}

export function cycleSortDir(
  currentKey: string | null | undefined,
  currentDir: "asc" | "desc" | undefined,
  nextKey: string,
): { sortKey: string | null; sortDir: "asc" | "desc" } {
  if (currentKey !== nextKey) return { sortKey: nextKey, sortDir: "asc" };
  if (currentDir === "asc") return { sortKey: nextKey, sortDir: "desc" };
  return { sortKey: null, sortDir: "asc" };
}
export interface DataTableProps<T> {
  columns: Column<T>[];
  data?: readonly T[] | T[];
  serverResponse?: ServerSideResponse<T> | T[] | null;
  expectedDraw?: number;
  onDataError?: (message: string) => void;
  suppressErrorAlert?: boolean;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  emptyText?: string;
  emptyIcon?: ReactNode;
  onRowClick?: (row: T, index: number) => void;
  rowKey?: (row: T, index: number) => string | number;
  keyField?: keyof T;
  emptyMessage?: string;
  compact?: boolean;
  frameless?: boolean;
  borderless?: boolean;
  maxHeight?: string;
  minTableWidth?: string;
  responsive?: boolean;
  tooltips?: boolean;
  tooltipMode?: "always" | "overflow";
  maxCellChars?: number;
  emptyValue?: ReactNode;
  page?: number;
  pageSize?: number;
  totalItems?: number;
  onPageChange?: (page: number) => void;
  itemLabel?: string;
  hidePageButtons?: boolean;
  /** Pagination footer style. `"compact"` is an alias for `pagerStyle="simple"`. */
  paginationVariant?: "buttons" | "compact";
  /** Center pager style. `"simple"` stays visible on a single page and enables the "Rows per page" input. */
  pagerStyle?: "numbered" | "simple";
  /** Shows a bounded "Rows per page" input; needs `pagerStyle="simple"`. */
  onPageSizeChange?: (size: number) => void;
  /** Renders "Rows per page" as a fixed-choice select instead of the free-text input. */
  pageSizeOptions?: number[];
  /** Render "Rows per page" as a numeric input (g360 style) instead of a dropdown. Defaults to true when pageSizeOptions is omitted. */
  pageSizeInput?: boolean;
  rowClassName?: (row: T, index: number) => string;
  rowTooltip?: (row: T, index: number) => string | undefined;
  sortKey?: string | null;
  sortDir?: "asc" | "desc";
  onSort?: (key: string) => void;
  sortIcon?: "chevron" | "arrows";
  enableCardView?: boolean;
  defaultViewMode?: "card" | "table";
  viewMode?: "card" | "table";
  onViewModeChange?: (v: "card" | "table") => void;
  renderCard?: (items: T[]) => ReactNode;
  cardExcludeKeys?: string[];
  renderExpanded?: (row: T, index: number) => ReactNode;
  isRowExpanded?: (row: T, index: number) => boolean;
}
export function ViewModeToggle({
  value,
  onChange,
}: {
  value: "card" | "table";
  onChange: (v: "card" | "table") => void;
}) {
  const btn = (mode: "card" | "table", label: string, icon: ReactNode) => {
    const active = value === mode;
    return (
      <Tip label={label}>
        <button
          type="button"
          aria-label={label}
          aria-pressed={active}
          onClick={() => onChange(mode)}
          className={`flex size-8 items-center justify-center rounded-md transition-colors ${active
              ? "bg-white dark:bg-white/10 text-indigo-600 dark:text-indigo-400 shadow-sm"
              : "text-[#64748b] dark:text-white/60 hover:text-foreground"
            }`}
        >
          {icon}
        </button>
      </Tip>
    );
  };
  return (
    <div className="inline-flex h-[38px] items-center gap-0.5 px-1 rounded-lg bg-[#f1f5f9] dark:bg-white/[0.04] border border-[#e2e8f0] dark:border-white/[0.06] flex-shrink-0">
      {btn("card", "Card view", <LayoutGrid className="size-4" />)}
      {btn("table", "Table view", <List className="size-4" />)}
    </div>
  );
}
function AutoCardGrid<T>({
  rows,
  columns,
  onRowClick,
  rowKey,
  excludeKeys,
  renderExpanded,
  isRowExpanded,
}: {
  rows: T[];
  columns: Column<T>[];
  onRowClick?: (row: T, index: number) => void;
  rowKey?: (row: T, index: number) => string | number;
  excludeKeys?: string[];
  renderExpanded?: (row: T, index: number) => ReactNode;
  isRowExpanded?: (row: T, index: number) => boolean;
}) {
  const visible = columns.filter(
    (c) =>
      !excludeKeys?.includes(c.key) &&
      c.key !== "no" &&
      c.key !== "sno" &&
      c.header !== "#",
  );
  const titleCol = visible[0];
  const detailCols = visible.slice(1);
  const extractHeaderText = (header: ReactNode): string => {
    if (typeof header === "string") return header;
    if (typeof header === "number") return String(header);
    return "";
  };
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 p-1">
      {rows.map((row, idx) => {
        const k = rowKey ? rowKey(row, idx) : idx;
        const titleContent = titleCol?.render
          ? titleCol.render(row, idx)
          : String((row as Record<string, unknown>)[titleCol?.key ?? ""] ?? "");
        return (
          <div
            key={k}
            onClick={onRowClick ? () => onRowClick(row, idx) : undefined}
            role={onRowClick ? "button" : undefined}
            tabIndex={onRowClick ? 0 : undefined}
            onKeyDown={
              onRowClick
                ? (e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onRowClick(row, idx);
                  }
                }
                : undefined
            }
            className={`group relative h-full rounded-2xl ${onRowClick ? "cursor-pointer" : ""} outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40`}
          >
            <div className="relative flex h-full flex-col rounded-2xl border-2 border-[#e8edf2] dark:border-white/20 bg-card dark:bg-indigo-500/10 overflow-hidden transition-all duration-300 group-hover:-translate-y-0.5 group-hover:border-indigo-400 dark:group-hover:border-indigo-500/60 group-hover:shadow-[0_12px_28px_-8px_rgba(79,70,229,0.28)] dark:group-hover:shadow-[0_0_28px_rgba(79,70,229,0.32)]">
              <div className="relative z-[1] flex flex-1 flex-col gap-3 p-4">
                {titleCol && (
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-bold leading-snug tracking-[-0.01em] text-foreground group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                      {titleContent}
                    </div>
                  </div>
                )}
                {detailCols.length > 0 && (
                  <div className="flex flex-col gap-2 mt-1">
                    {detailCols.map((col) => {
                      const val = col.render
                        ? col.render(row, idx)
                        : String((row as Record<string, unknown>)[col.key] ?? "");
                      const label = extractHeaderText(col.header);
                      return (
                        <div
                          key={col.key}
                          className="flex items-start justify-between gap-3 text-[12px]"
                        >
                          {label && (
                            <span className="font-semibold text-[#64748b] dark:text-white/60 whitespace-nowrap">
                              {label}
                            </span>
                          )}
                          <div
                            className="flex-1 min-w-0 text-right text-[#0f172a] dark:text-white/85"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {val}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              {renderExpanded && isRowExpanded?.(row, idx) && (
                <div
                  className="border-t border-[#e8edf2] px-4 py-3 dark:border-white/10"
                  onClick={(e) => e.stopPropagation()}
                >
                  {renderExpanded(row, idx)}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
const alignCls = (a?: "left" | "center" | "right") =>
  a === "left" ? "text-left" : a === "right" ? "text-right" : "text-center";
function isSerialColumn<T>(col: Column<T>): boolean {
  const headerChildren =
    col.header && typeof col.header === "object" && "props" in col.header
      ? (col.header as { props?: { children?: unknown } }).props?.children
      : undefined;
  if (col.header === "#" || headerChildren === "#") return true;
  const keyLc = String(col.key).toLowerCase();
  return (
    keyLc.endsWith("sno") ||
    ["idx", "index", "sno", "serial", "no", "#"].includes(keyLc)
  );
}
export function isCssWidth(w: string | undefined): w is string {
  if (!w) return false;

  return /^(calc\(.+\)|\d+(\.\d+)?(px|%|r?em|v[wh]|ch|pt|cm|mm|in))$/i.test(
    w.trim(),
  );
}
const DEFAULT_COL_MIN_WIDTH = 120;
const SERIAL_COL_MIN_WIDTH = 60;
const columnMinWidth = <T,>(col: Column<T>): number =>
  col.minWidth ?? (isSerialColumn(col) ? SERIAL_COL_MIN_WIDTH : DEFAULT_COL_MIN_WIDTH);
export function computeMinTableWidth<T>(
  columns: Column<T>[],
  fixed: boolean,
): number {
  const floors = columns.map((c) => columnMinWidth(c));
  const stacked = floors.reduce((a, b) => a + b, 0);
  if (!fixed) return stacked;
  let pxSum = 0;
  let pctTotal = 0;
  let pctDriven = 0;
  let unsized = 0;
  let widestUnsizedFloor = 0;
  columns.forEach((col, i) => {
    const floor = floors[i];
    const w = col.width?.trim();
    if (!isCssWidth(w)) {
      unsized += 1;
      widestUnsizedFloor = Math.max(widestUnsizedFloor, floor);
      return;
    }
    const px = /^(\d+(?:\.\d+)?)px$/i.exec(w);
    const pct = /^(\d+(?:\.\d+)?)%$/.exec(w);
    if (px) {
      pxSum += Math.max(parseFloat(px[1]), col.minWidth ?? 0);
    } else if (pct) {
      const share = parseFloat(pct[1]);
      pctTotal += share;
      if (share > 0) pctDriven = Math.max(pctDriven, (floor * 100) / share);
    } else {
      pxSum += floor;
    }
  });
  const leftoverShare = 1 - Math.min(pctTotal, 90) / 100;
  const evenSplitDriven =
    (pxSum + unsized * widestUnsizedFloor) / leftoverShare;
  return Math.ceil(Math.max(evenSplitDriven, pctDriven));
}
const ellipsisCls = (e: Column<unknown>["ellipsis"]) => {
  if (e === true) return "truncate";
  if (e === 1) return "line-clamp-1";
  if (e === 2) return "line-clamp-2";
  if (e === 3) return "line-clamp-3";
  return "";
};
const SIBLINGS = 2;
export const getPageWindow = (
  page: number,
  totalPages: number,
): (number | "dots")[] => {
  const maxVisible = 2 * SIBLINGS + 5;
  if (totalPages <= maxVisible) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const current = page + 1;
  const out: (number | "dots")[] = [1];
  const start = Math.max(2, current - SIBLINGS);
  const end = Math.min(totalPages - 1, current + SIBLINGS);
  if (start > 2) out.push("dots");
  for (let p = start; p <= end; p++) out.push(p);
  if (end < totalPages - 1) out.push("dots");
  out.push(totalPages);
  return out;
};
function DataTableInner<T>({
  columns,
  data,
  serverResponse,
  expectedDraw,
  onDataError,
  suppressErrorAlert = false,
  loading = false,
  error = null,
  onRetry,
  emptyText: emptyTextProp,
  emptyMessage,
  emptyIcon,
  onRowClick,
  rowKey,
  keyField,
  compact = false,
  frameless = false,
  borderless = false,
  maxHeight,
  minTableWidth,
  responsive = true,
  tooltips = true,
  tooltipMode = "overflow",
  maxCellChars = DEFAULT_MAX_CELL_CHARS,
  emptyValue = "-",
  page,
  pageSize = DEFAULT_TABLE_PAGE_SIZE,
  totalItems,
  onPageChange,
  itemLabel = "records",
  hidePageButtons = false,
  paginationVariant = "buttons",
  pagerStyle = "simple",
  onPageSizeChange,
  pageSizeOptions,
  pageSizeInput = true,
  rowClassName,
  rowTooltip,
  sortKey,
  sortDir = "asc",
  onSort,
  sortIcon = "arrows",
  enableCardView = false,
  defaultViewMode = "card",
  viewMode: viewModeProp,
  onViewModeChange,
  renderCard,
  cardExcludeKeys,
  renderExpanded,
  isRowExpanded,
}: DataTableProps<T>) {
  const emptyText = emptyTextProp ?? emptyMessage ?? "No Data Available";
  const getRowKey = rowKey
    ? rowKey
    : keyField
      ? (row: T) => String((row as Record<string, unknown>)[String(keyField)])
      : undefined;
  const [internalViewMode, setInternalViewMode] = useState<"card" | "table">(
    defaultViewMode,
  );
  const isControlled = viewModeProp !== undefined;
  const viewMode = isControlled ? viewModeProp : internalViewMode;
  const setViewMode = (v: "card" | "table") => {
    if (!isControlled) setInternalViewMode(v);
    onViewModeChange?.(v);
  };
  const canToggleView = enableCardView || !!renderCard;
  const activeView: "card" | "table" = canToggleView ? viewMode : "table";
  const showInternalToggle = canToggleView && !isControlled;
  const server = serverResponse !== undefined
    ? normalizeServerResponse<T>(serverResponse, expectedDraw)
    : null;
  const dataError = server
    ? server.error
    : data !== undefined && !Array.isArray(data)
      ? `Table data must be an array, but received ${describeValue(data)}.`
      : null;
  const rows: T[] = server
    ? server.rows
    : Array.isArray(data)
      ? data
      : [];
  const effectiveError = error ?? dataError;
  const isShapeError =
    !error && (server ? server.errorKind === "shape" : !!dataError);
  const shouldAlert = !!effectiveError && (isShapeError || !suppressErrorAlert);
  const isStaleResponse = !!server?.stale;
  const colCount = Math.max(1, columns.length);
  const safePageSize = pageSize > 0 ? pageSize : DEFAULT_TABLE_PAGE_SIZE;
  const total =
    server && server.countsKnown
      ? server.recordsFiltered
      : (totalItems ?? server?.recordsFiltered ?? rows.length);
  const totalPages = Math.max(1, Math.ceil(total / safePageSize));
  const currentPage = Math.min(Math.max(page ?? 0, 0), totalPages - 1);
  const hasPagination =
    page !== undefined && onPageChange !== undefined && total > 0;
  const useSimplePager =
    pagerStyle === "simple" || paginationVariant === "compact";
  // Rows-per-page shows for either pager style now — the control used to be
  // wired only under pagerStyle="simple", silently hiding it on tables that
  // set onPageSizeChange but kept the default "numbered" style.
  const showRowsPerPage = !!onPageSizeChange;
  page !== undefined &&
    onPageChange !== undefined &&
    total > 0 &&
    !effectiveError &&
    !isStaleResponse;
  const activeSortKey =
    sortKey && columns.some((c) => c.key === sortKey && c.sortable)
      ? sortKey
      : null;
  const activeSortDir: "asc" | "desc" = sortDir === "desc" ? "desc" : "asc";
  const [navPending, setNavPending] = useState(false);
  const dataIdentity = server ?? data;
  useEffect(() => {
    setNavPending(false);
  }, [dataIdentity, loading]);

  useEffect(() => {
    if (!navPending) return;

    const timer = setTimeout(() => {
      setNavPending(false);
    }, 15000);

    return () => {
      clearTimeout(timer);
    };
  }, [navPending]);
  const withNavPending = (run: () => void) => {
    setNavPending(true);
    run();
  };
  const showNavVeil = loading || (navPending && rows.length > 0);
  const safeSort = (key: string) => {
    try {
      withNavPending(() => onSort?.(key));
    } catch {
    }
  };
  const hasExplicitWidths = columns.some((c) => isCssWidth(c.width));
  const autoMinWidth = computeMinTableWidth(columns, hasExplicitWidths);
  const resolvedMinWidth =
    minTableWidth ?? (responsive ? `${autoMinWidth}px` : undefined);
  const cellPadX = compact ? "px-4" : "px-5";
  const cellPad = compact ? `${cellPadX} py-2.5` : `${cellPadX} py-4`;
  const onDataErrorRef = useRef(onDataError);
  onDataErrorRef.current = onDataError;
  useEffect(() => {
    if (!effectiveError) return;
    if (shouldAlert) {
      useToastStore
        .getState()
        .showError(
          isShapeError ? "Invalid table data" : "Error",
          effectiveError,
        );
    }
    if (isShapeError) onDataErrorRef.current?.(effectiveError);
  }, [effectiveError, isShapeError, shouldAlert]);
  return (
    <div className="flex flex-col min-h-0 flex-1 gap-2">
      {showInternalToggle && (
        <div className="flex-shrink-0 flex items-center justify-end">
          <ViewModeToggle value={activeView} onChange={setViewMode} />
        </div>
      )}
      <div
        className={
          borderless || frameless || activeView === "card"
            ? "relative w-full flex flex-col min-h-0 flex-1 overflow-hidden"
            : "relative w-full rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] bg-white dark:bg-[#131320] shadow-sm overflow-hidden flex flex-col min-h-0 flex-1"
        }
      >
        {showNavVeil && <LoadingOverlay />}
        <div aria-busy={showNavVeil} className="contents">
          {activeView === "card" ? (
            <div
              className="w-full overflow-auto flex-1 min-h-0"
              style={{ maxHeight }}
            >
              {loading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 pt-2 px-1">
                  {Array.from({ length: 8 }).map((_, i) => (
                    <div
                      key={i}
                      className="animate-pulse rounded-2xl border-2 border-[#e2e8f0] dark:border-white/[0.08] bg-white dark:bg-white/[0.03] p-3.5 flex flex-col gap-3"
                    >
                      <div className="flex items-start justify-between gap-2.5">
                        <div className="h-3.5 rounded bg-[#e2e8f0] dark:bg-white/[0.09] w-3/4" />
                        <div className="size-4 rounded bg-[#e2e8f0] dark:bg-white/[0.09]" />
                      </div>
                      <div className="flex gap-1.5">
                        <div className="h-4 rounded-md bg-[#e2e8f0] dark:bg-white/[0.09] w-16" />
                        <div className="h-4 rounded-md bg-[#e2e8f0] dark:bg-white/[0.09] w-10" />
                      </div>
                      <div className="mt-auto flex flex-col gap-3">
                        <div className="h-14 rounded-xl bg-[#f1f5f9] dark:bg-white/[0.05] border border-[#edf0f4] dark:border-white/[0.07]" />
                        <div className="flex flex-col gap-1.5">
                          <div className="h-2.5 rounded bg-[#e2e8f0] dark:bg-white/[0.09] w-2/3" />
                          <div className="h-2.5 rounded bg-[#e2e8f0] dark:bg-white/[0.09] w-1/2" />
                        </div>
                        <div className="flex items-center justify-between pt-3 border-t border-[#eef2f6] dark:border-white/[0.06]">
                          <div className="h-4 rounded-full bg-[#e2e8f0] dark:bg-white/[0.09] w-20" />
                          <div className="flex gap-1">
                            <div className="size-6 rounded-md bg-[#e2e8f0] dark:bg-white/[0.09]" />
                            <div className="size-6 rounded-md bg-[#e2e8f0] dark:bg-white/[0.09]" />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : effectiveError ? (
                <div
                  role="alert"
                  className="flex flex-col items-center justify-center gap-3 py-16 rounded-2xl border border-dashed border-red-200 dark:border-red-500/30 bg-red-50/40 dark:bg-red-500/[0.05]"
                >
                  <div className="size-10 rounded-full bg-red-100 dark:bg-red-500/20 flex items-center justify-center">
                    <AlertCircle className="size-5 text-red-600 dark:text-red-400" />
                  </div>
                  <div className="flex flex-col items-center gap-1 max-w-md text-center px-4">
                    <p className="text-[13px] font-semibold text-red-700 dark:text-red-300">
                      {isShapeError
                        ? "This data could not be displayed."
                        : "Something went wrong."}
                    </p>
                    <p className="text-[12px] text-[#64748b] dark:text-white/60 break-words">
                      {effectiveError}
                    </p>
                  </div>
                  {onRetry && (
                    <button
                      onClick={onRetry}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-200 dark:border-red-500/30 bg-white dark:bg-white/[0.04] text-[12px] font-semibold text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-500/10 transition-colors"
                    >
                      <RefreshCw className="size-3.5" />
                      Retry
                    </button>
                  )}
                </div>
              ) : rows.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-2 py-16">
                  <div className="size-10 rounded-full bg-[#f1f5f9] dark:bg-white/[0.05] flex items-center justify-center">
                    {emptyIcon ?? (
                      <Shield className="size-4 text-[#64748b] dark:text-white/50" />
                    )}
                  </div>
                  <p className="text-[13px] font-medium text-[#475569] dark:text-white/50">
                    {emptyText}
                  </p>
                </div>
              ) : renderCard ? (
                renderCard(rows)
              ) : (
                <AutoCardGrid
                  rows={rows}
                  columns={columns}
                  onRowClick={onRowClick}
                  rowKey={getRowKey}
                  excludeKeys={cardExcludeKeys}
                  renderExpanded={renderExpanded}
                  isRowExpanded={isRowExpanded}
                />
              )}
            </div>
          ) : (
            <div
              className="datatable-scroll w-full overflow-auto overscroll-x-contain overscroll-y-auto flex-1 min-h-0"
              style={{ maxHeight }}
            >
              <table
                style={{
                  minWidth: resolvedMinWidth ? resolvedMinWidth : undefined,
                  borderCollapse: "collapse",
                }}
                className={`w-full min-w-full ${hasExplicitWidths ? "table-fixed" : "table-auto"} text-[13px]`}
              >
                {hasExplicitWidths && (
                  <colgroup>
                    {columns.map((col, ci) => (
                      <col
                        key={`${String(col.key)}-${ci}`}
                        style={
                          isCssWidth(col.width) ? { width: col.width } : undefined
                        }
                      />
                    ))}
                  </colgroup>
                )}
                <thead className={`sticky top-0 z-20 ${borderless ? "bg-card" : "bg-[#eef2f7] dark:bg-[#1b1c2e]"}`}>
                  <tr className={`border-b ${borderless ? "border-border/50 bg-card" : "border-[#e2e8f0] dark:border-white/[0.06] bg-[#eef2f7] dark:bg-white/[0.05]"}`}>
                    {columns.map((col, ci) => {
                      const isSortable = !!col.sortable && !!onSort;
                      const isActive = activeSortKey === col.key;
                      const headerAlign = col.headerAlign ?? col.align ?? "left";
                      const headerText = flatten(nodeText(col.header));
                      const headerTip =
                        tooltips && col.headerTooltip !== false
                          ? typeof col.headerTooltip === "string"
                            ? col.headerTooltip
                            : headerText || undefined
                          : undefined;
                      return (
                        <th
                          key={`${String(col.key)}-${ci}`}
                          scope="col"
                          aria-sort={
                            isSortable
                              ? isActive
                                ? activeSortDir === "asc"
                                  ? "ascending"
                                  : "descending"
                                : "none"
                              : undefined
                          }
                          tabIndex={isSortable ? 0 : undefined}
                          className={`${cellPadX} py-3.5 text-[13px] font-medium sticky top-0 z-20 ${
                            borderless
                              ? "text-muted-foreground hover:text-foreground bg-card"
                              : "text-[#1e293b] dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 bg-[#eef2f7] dark:bg-[#1b1c2e]"
                          } whitespace-nowrap cursor-pointer select-none transition-colors ${alignCls(headerAlign)} ${
                            ci > 0 && !borderless ? "border-l border-[#e2e8f0] dark:border-white/[0.06]" : ""
                          }
                      ${col.headerClassName ?? ""} ${isSortable ? "outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40" : ""}`}
                          onClick={isSortable ? () => safeSort(col.key) : undefined}
                          onKeyDown={
                            isSortable
                              ? (e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  safeSort(col.key);
                                }
                              }
                              : undefined
                          }
                        >
                          <TooltipCell
                            tooltip={headerTip}
                            mode="always"
                            wrapperClass="block w-full min-w-0"
                          >
                            {isSortable ? (
                              (() => {
                                const icon =
                                  sortIcon === "arrows" ? (
                                    <span
                                      className="inline-flex flex-col items-center select-none shrink-0"
                                      style={{ fontSize: "7px", lineHeight: "7px" }}
                                    >
                                      <span
                                        className={`font-black ${isActive && activeSortDir === "asc" ? "text-indigo-600 dark:text-white" : "text-[#94a3b8] dark:text-white/40"}`}
                                      >
                                        {SORT_ARROW_UP}
                                      </span>
                                      <span
                                        className={`font-black ${isActive && activeSortDir === "desc" ? "text-indigo-600 dark:text-white" : "text-[#94a3b8] dark:text-white/40"}`}
                                      >
                                        {SORT_ARROW_DOWN}
                                      </span>
                                    </span>
                                  ) : isActive ? (
                                    activeSortDir === "asc" ? (
                                      <ChevronUp className="size-3.5 shrink-0 text-indigo-600 dark:text-indigo-400" />
                                    ) : (
                                      <ChevronDown className="size-3.5 shrink-0 text-indigo-600 dark:text-indigo-400" />
                                    )
                                  ) : (
                                    <ChevronsUpDown className="size-3.5 shrink-0 text-[#94a3b8] dark:text-white/30" />
                                  );
                                if (headerAlign === "right") {
                                  return col.sortIconAfterText || borderless ? (
                                    <span className="inline-flex items-center gap-1.5 justify-end">
                                      {col.header}
                                      {icon}
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center gap-1.5 justify-end">
                                      {icon}
                                      {col.header}
                                    </span>
                                  );
                                }
                                if (headerAlign === "left") {
                                  return (
                                    <span className="inline-flex items-center gap-1.5 justify-start">
                                      {col.header}
                                      {icon}
                                    </span>
                                  );
                                }
                                return (
                                  <span className="inline-flex items-center gap-1.5 justify-center w-full">
                                    {col.header}
                                    {icon}
                                  </span>
                                );
                              })()
                            ) : (
                              col.header
                            )}
                          </TooltipCell>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={colCount} className="px-5 py-14 text-center">
                        <div className="flex items-center justify-center gap-3">
                          <Loader2 className="size-5 animate-spin text-indigo-500 dark:text-indigo-400" />
                          <span className="text-[13px] text-[#64748b] dark:text-white/50">
                            Loading…
                          </span>
                        </div>
                      </td>
                    </tr>
                  ) : effectiveError ? (
                    <tr>
                      <td colSpan={colCount} className="px-5 py-14 text-center">
                        <div
                          role="alert"
                          className="flex flex-col items-center gap-3"
                        >
                          <div className="size-10 rounded-full bg-red-100 dark:bg-red-500/20 flex items-center justify-center">
                            <AlertCircle className="size-5 text-red-600 dark:text-red-400" />
                          </div>
                          <div className="flex flex-col items-center gap-1 max-w-md">
                            <p className="text-[13px] font-semibold text-red-700 dark:text-red-300">
                              {isShapeError
                                ? "This data could not be displayed."
                                : "Something went wrong."}
                            </p>
                            <p className="text-[12px] text-[#64748b] dark:text-white/60 break-words">
                              {effectiveError}
                            </p>
                          </div>
                          {onRetry && (
                            <button
                              onClick={onRetry}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-200 dark:border-red-500/30 bg-white dark:bg-white/[0.04] text-[12px] font-semibold text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-500/10 transition-colors"
                            >
                              <RefreshCw className="size-3.5" />
                              Retry
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={colCount} className="px-5 py-14 text-center">
                        <div className="flex flex-col items-center gap-2">
                          <div className="size-10 rounded-full bg-[#f1f5f9] dark:bg-white/[0.05] flex items-center justify-center">
                            {emptyIcon ?? (
                              <Shield className="size-4 text-[#64748b] dark:text-white/50" />
                            )}
                          </div>
                          <p className="text-[13px] font-medium text-[#475569] dark:text-white/50">
                            {emptyText}
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    rows.map((row, idx) => {
                      let rowK: string | number = idx;
                      try {
                        if (getRowKey) rowK = getRowKey(row, idx);
                      } catch {
                        rowK = idx;
                      }
                      let rowTip: string | undefined;
                      try {
                        rowTip = rowTooltip ? rowTooltip(row, idx) : undefined;
                      } catch {
                        rowTip = undefined;
                      }
                      const rowEl = (
                        <tr
                          key={rowK}
                          onClick={
                            onRowClick ? () => onRowClick(row, idx) : undefined
                          }
                          className={`border-b ${borderless ? "border-border/30 hover:bg-muted/30" : "border-[#e2e8f0] dark:border-white/[0.06] hover:bg-indigo-50/50 dark:hover:bg-white/[0.05]"} transition-[background-color,box-shadow] duration-150 ease-out group
                    ${onRowClick ? "cursor-pointer" : ""}
                    ${rowClassName ? rowClassName(row, idx) : ""}`}
                        >
                          {columns.map((col, ci) => {
                            const record = (row ?? {}) as Record<string, unknown>;
                            const val = record[String(col.key)];
                            let content: ReactNode;
                            try {
                              content = col.render
                                ? col.render(row, idx)
                                : isEmptyContent(val)
                                  ? null
                                  : String(val);
                            } catch {
                              content = null;
                            }
                            const isBlank = isEmptyContent(content);
                            const cellText = isBlank ? "" : flatten(nodeText(content));
                            if (isBlank) {
                              content = (
                                <span
                                  className="text-[#94a3b8] dark:text-white/35"
                                  aria-label="No value"
                                >
                                  {col.emptyValue ?? emptyValue}
                                </span>
                              );
                            }
                            const isSerialCol = isSerialColumn(col);
                            const charLimit =
                              col.maxChars === false
                                ? Number.POSITIVE_INFINITY
                                : (col.maxChars ?? maxCellChars);
                            const isTruncated =
                              !isBlank &&
                              Number.isFinite(charLimit) &&
                              charLimit > 0 &&
                              cellText.length > charLimit;
                            if (isTruncated) {
                              content = truncateNode(content, charLimit)[0];
                            }
                            let tip: string | undefined;
                            if (tooltips && col.tooltip !== false && !isSerialCol) {
                              try {
                                if (typeof col.tooltip === "function")
                                  tip = col.tooltip(row);
                                else if (typeof col.tooltip === "string")
                                  tip = col.tooltip;
                                else if (!isBlank) tip = cellText || undefined;
                              } catch {
                                tip = undefined;
                              }
                            }
                            const clamp = ellipsisCls(col.ellipsis);
                            const alignWrap = `flex min-w-0 overflow-hidden ${col.align === "right"
                                ? "justify-end"
                                : col.align === "left"
                                  ? "justify-start"
                                  : "justify-center"
                              }`;
                            return (
                              <td
                                key={`${String(col.key)}-${ci}`}
                                className={`${cellPad} ${alignCls(col.align)} text-[#0f172a] dark:text-white/80 align-middle ${hasExplicitWidths ? "" : "max-w-xs"} ${isSerialCol ? "whitespace-nowrap" : "whitespace-normal break-words"} ${ci > 0 && !borderless ? "border-l border-[#e2e8f0] dark:border-white/[0.06]" : ""} ${col.cellClassName ?? ""}`}
                              >
                                <div className={alignWrap}>
                                  <TooltipCell
                                    tooltip={tip}
                                    clampClass={clamp}
                                    wrapperClass={col.wrapperClass}
                                    mode={isTruncated ? "always" : tooltipMode}
                                  >
                                    {content}
                                  </TooltipCell>
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      );
                      const rowWithTip = rowTip ? (
                        <Tooltip key={rowK}>
                          <TooltipTrigger asChild>{rowEl}</TooltipTrigger>
                          <TooltipContent side="top" sideOffset={6} className="pointer-events-none">
                            {rowTip}
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        rowEl
                      );
                      const expanded =
                        renderExpanded && isRowExpanded?.(row, idx)
                          ? renderExpanded(row, idx)
                          : null;
                      return expanded ? (
                        <Fragment key={`${rowK}-group`}>
                          {rowWithTip}
                          <tr className="border-b border-[#e2e8f0] dark:border-white/[0.06] bg-[#f8fafc] dark:bg-white/[0.02]">
                            <td
                              colSpan={columns.length}
                              className="min-w-0 overflow-hidden px-4 py-3"
                            >
                              <div className="min-w-0 overflow-hidden">
                                {expanded}
                              </div>
                            </td>
                          </tr>
                        </Fragment>
                      ) : (
                        rowWithTip
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
          {hasPagination && (
            <div
              className={`flex items-center justify-between gap-3 px-4 py-2 flex-shrink-0 bg-[#fafbfc] dark:bg-white/[0.015] ${
                activeView === "card"
                  ? "rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] mt-2"
                  : "border-t border-[#e2e8f0] dark:border-white/[0.06]"
              }`}
            >
              <p className="text-[11px] leading-tight text-[#475569] dark:text-white/70 whitespace-nowrap">
                Showing{" "}
                <span className="font-semibold text-[#334155] dark:text-white/90">
                  {total === 0 ? 0 : currentPage * safePageSize + 1}
                </span>{" "}
                to{" "}
                <span className="font-semibold text-[#334155] dark:text-white/90">
                  {Math.min((currentPage + 1) * safePageSize, total)}
                </span>{" "}
                of{" "}
                <span className="font-semibold text-[#334155] dark:text-white/90">
                  {total}
                </span>{" "}
                {itemLabel}
                {server && server.recordsTotal > server.recordsFiltered && (
                  <span className="text-[#94a3b8] dark:text-white/45">
                    {" "}
                    (filtered from {server.recordsTotal})
                  </span>
                )}
              </p>

              {!hidePageButtons && (
                <div className="flex flex-1 items-center justify-center">
                  {useSimplePager ? (
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        disabled={currentPage === 0}
                        onClick={() => withNavPending(() => onPageChange!(currentPage - 1))}
                        aria-label="Previous page"
                        className="flex size-8 items-center justify-center rounded-lg border border-[#e2e8f0] dark:border-white/[0.08] text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.05] hover:border-[#cbd5e1] dark:hover:border-white/15 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors cursor-pointer"
                      >
                        <ChevronLeft className="size-4" />
                      </button>
                      <NumericInput
                        value={currentPage + 1}
                        min={1}
                        max={totalPages}
                        onCommit={(n) =>
                          withNavPending(() =>
                            onPageChange!(Math.min(Math.max(n, 1), totalPages) - 1)
                          )
                        }
                        ariaLabel="Page number"
                        width="w-14"
                      />
                      <span className="px-1 text-[11px] text-[#475569] dark:text-white/70">
                        of{" "}
                        <span className="font-semibold text-[#334155] dark:text-white/90">
                          {totalPages}
                        </span>
                      </span>
                      <button
                        type="button"
                        disabled={currentPage >= totalPages - 1}
                        onClick={() => withNavPending(() => onPageChange!(currentPage + 1))}
                        aria-label="Next page"
                        className="flex size-8 items-center justify-center rounded-lg border border-[#e2e8f0] dark:border-white/[0.08] text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.05] hover:border-[#cbd5e1] dark:hover:border-white/15 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors cursor-pointer"
                      >
                        <ChevronRight className="size-4" />
                      </button>
                    </div>
                  ) : totalPages > 1 ? (
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        disabled={currentPage === 0}
                        onClick={() => withNavPending(() => onPageChange!(currentPage - 1))}
                        aria-label="Previous page"
                        className="flex size-8 items-center justify-center rounded-lg border border-[#e2e8f0] dark:border-white/[0.08] text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.05] hover:border-[#cbd5e1] dark:hover:border-white/15 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors cursor-pointer"
                      >
                        <ChevronLeft className="size-4" />
                      </button>
                      {getPageWindow(currentPage, totalPages).map((p, idx) =>
                        p === "dots" ? (
                          <span
                            key={`dots-${idx}`}
                            className="flex size-8 items-center justify-center text-xs text-[#94a3b8] dark:text-white/40 select-none"
                          >
                            …
                          </span>
                        ) : (
                          <button
                            type="button"
                            key={p}
                            onClick={() => withNavPending(() => onPageChange!(p - 1))}
                            className={`flex size-8 items-center justify-center text-xs font-semibold rounded-lg border transition-colors ${
                              p - 1 === currentPage
                                ? "bg-indigo-600 border-indigo-600 text-white shadow-sm"
                                : "border-[#e2e8f0] dark:border-white/[0.08] text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.05] hover:border-[#cbd5e1] dark:hover:border-white/15"
                            }`}
                          >
                            {p}
                          </button>
                        ),
                      )}
                      <button
                        type="button"
                        disabled={currentPage >= totalPages - 1}
                        onClick={() => withNavPending(() => onPageChange!(currentPage + 1))}
                        aria-label="Next page"
                        className="flex size-8 items-center justify-center rounded-lg border border-[#e2e8f0] dark:border-white/[0.08] text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.05] hover:border-[#cbd5e1] dark:hover:border-white/15 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors cursor-pointer"
                      >
                        <ChevronRight className="size-4" />
                      </button>
                    </div>
                  ) : null}
                </div>
              )}

              <div className="flex items-center justify-end">
                {showRowsPerPage &&
                  (pageSizeOptions && !pageSizeInput ? (
                    <label className="flex items-center gap-2 text-[11px] text-[#475569] dark:text-white/70 whitespace-nowrap">
                      <span>Rows per page</span>
                      <select
                        value={safePageSize}
                        onChange={(e) => onPageSizeChange!(Number(e.target.value))}
                        aria-label="Rows per page"
                        className="rounded-lg border border-[#e2e8f0] dark:border-white/[0.08] bg-white dark:bg-white/[0.04] px-2 py-1 font-semibold text-[#334155] dark:text-white/90 outline-none focus:border-indigo-500 dark:focus:border-indigo-500"
                      >
                        {pageSizeOptions.map((n) => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </select>
                    </label>
                  ) : (
                    <label className="flex items-center gap-2 text-[11px] text-[#475569] dark:text-white/70 whitespace-nowrap">
                      <span>Rows per page</span>
                      <NumericInput
                        value={safePageSize}
                        min={1}
                        max={Math.max(100, total)}
                        onCommit={onPageSizeChange!}
                        ariaLabel="Rows per page"
                        width="w-16"
                      />
                    </label>
                  ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
interface TableErrorBoundaryProps {
  children: ReactNode;
  resetKey?: string;
  onError?: (message: string) => void;
}
class TableErrorBoundary extends Component<
  TableErrorBoundaryProps,
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("DataTable render failed:", error, info.componentStack);
    useToastStore
      .getState()
      .showError(
        "Table could not be displayed",
        error.message || "An unexpected error occurred while rendering.",
      );
    this.props.onError?.(error.message);
  }
  componentDidUpdate(prev: TableErrorBoundaryProps) {
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }
  private retry = () => this.setState({ error: null });
  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <div className="w-full rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] bg-white dark:bg-[#131320] shadow-sm">
        <div
          role="alert"
          className="flex flex-col items-center justify-center gap-3 px-5 py-14 text-center"
        >
          <div className="flex size-10 items-center justify-center rounded-full bg-red-100 dark:bg-red-500/20">
            <AlertCircle className="size-5 text-red-600 dark:text-red-400" />
          </div>
          <div className="flex max-w-md flex-col items-center gap-1">
            <p className="text-[13px] font-semibold text-red-700 dark:text-red-300">
              This table could not be displayed.
            </p>
            <p className="text-[12px] break-words text-[#64748b] dark:text-white/60">
              {error.message || "An unexpected error occurred."}
            </p>
          </div>
          <button
            onClick={this.retry}
            className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-[12px] font-semibold text-red-700 transition-colors hover:bg-red-100 dark:border-red-500/30 dark:bg-white/[0.04] dark:text-red-300 dark:hover:bg-red-500/10"
          >
            <RefreshCw className="size-3.5" />
            Retry
          </button>
        </div>
      </div>
    );
  }
}
export function DataTable<T>(props: DataTableProps<T>) {
  const rowCount = Array.isArray(props.serverResponse)
    ? props.serverResponse.length
    : Array.isArray(
      (props.serverResponse as ServerSideResponse<T> | undefined)?.data,
    )
      ? ((props.serverResponse as ServerSideResponse<T>).data as T[]).length
      : Array.isArray(props.data)
        ? props.data.length
        : 0;
  return (
    <TableErrorBoundary
      onError={props.onDataError}
      resetKey={`${props.page ?? 0}|${props.expectedDraw ?? 0}|${rowCount}|${props.sortKey ?? ""}|${props.sortDir ?? ""}`}
    >
      <DataTableInner {...props} />
    </TableErrorBoundary>
  );
}

function trim(): any {
  throw new Error("Function not implemented.");
}
